var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import KeyCode from 'rc-util/lib/KeyCode';
import pickAttrs from 'rc-util/lib/pickAttrs';
import useMemo from 'rc-util/lib/hooks/useMemo';
import classNames from 'classnames';
import OutlinedRight from 'acud-icon/lib/icons/OutlinedRight';
import OutlinedDown from 'acud-icon/lib/icons/OutlinedDown';
import { toRawString } from './utils/valueUtil';
import List from '../../virtual-list';
import Checkbox from '../../checkbox';
import { useLocaleReceiver } from '../../locale-provider/LocaleReceiver';
const formatContent = (content, searchValue, prefixCls) => {
    const lowerContent = toRawString(content).toLowerCase();
    const lowerSearch = searchValue.toLowerCase();
    const searchStartIndex = lowerContent.indexOf(lowerSearch);
    const len = lowerSearch.length;
    // 存在搜索值不在content的情况，比如value与children不一致时
    if (searchStartIndex === -1) {
        return <>{content}</>;
    }
    return (<>
        {content.toString().slice(0, searchStartIndex)}
        <span className={`${prefixCls}-search-highlight`}>
            {content.toString().slice(searchStartIndex, searchStartIndex + len)}
        </span>
        {content.toString().slice(searchStartIndex + len)}
    </>);
};
/**
 * {afterSearch}
 * ;list of option display.
 * Will fallback to dom if use customize render.
 */
// const OptionList: React.RefForwardingComponent<
const OptionList = ({ prefixCls, id, flattenOptions, childrenAsData, values, searchValue, multiple, defaultActiveFirstOption, height, itemHeight, notFoundContent, mode, open, virtual, keepExpand = false, defaultExpandGroupKey = false, showTitle = false, showSelectAll = true, optionRender, renderEmpty, onSelect, onToggleOpen, onActiveValue, onScroll, onMouseEnter, triggerChange, setInnerOpen, onExpandChange, cleanSearchValue }, ref) => {
    const itemPrefixCls = `${prefixCls}-item`;
    const [selectLocale] = useLocaleReceiver('Select');
    const memoFlattenOptions = useMemo(() => flattenOptions, [open, flattenOptions], (prev, next) => {
        return next[0] && prev[1] !== next[1];
    });
    // =========================== List ===========================
    const listRef = React.useRef(null);
    const onListMouseDown = event => {
        event.preventDefault();
    };
    const scrollIntoView = (index) => {
        if (listRef.current) {
            listRef.current.scrollTo({ index });
        }
    };
    // ========================== Active ==========================
    const getEnabledActiveIndex = (index, offset = 1) => {
        const len = memoFlattenOptions.length;
        for (let i = 0; i < len; i += 1) {
            const current = (index + i * offset + len) % len;
            const { group, data } = memoFlattenOptions[current];
            if (!group && !data.disabled) {
                return current;
            }
        }
        return -1;
    };
    const [activeIndex, setActiveIndex] = React.useState(() => getEnabledActiveIndex(0));
    const setActive = (index, fromKeyboard = false) => {
        setActiveIndex(index);
        const info = { source: fromKeyboard ? 'keyboard' : 'mouse' };
        // Trigger active event
        const flattenItem = memoFlattenOptions[index];
        if (!flattenItem) {
            onActiveValue(null, -1, info);
            return;
        }
        onActiveValue(flattenItem.data.value, index, info);
    };
    // Auto active first item when list length or searchValue changed
    React.useEffect(() => {
        setActive(defaultActiveFirstOption !== false ? getEnabledActiveIndex(0) : -1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [memoFlattenOptions.length, searchValue]);
    // Auto scroll to item position in single mode
    React.useEffect(() => {
        var _a;
        /**
         * React will skip `onChange` when component update.
         * `setActive` function will call root accessibility state update which makes re-render.
         * So we need to delay to let Input component trigger onChange first.
         */
        const timeoutId = setTimeout(() => {
            if (!multiple && open && values.size === 1) {
                const value = Array.from(values)[0];
                const index = memoFlattenOptions.findIndex(({ data }) => data.value === value);
                setActive(index);
                scrollIntoView(index);
            }
        });
        // Force trigger scrollbar visible when open
        if (open) {
            (_a = listRef.current) === null || _a === void 0 ? void 0 : _a.scrollTo(undefined);
        }
        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    // ========================== Values ==========================
    const onSelectValue = (value) => {
        if (value !== undefined) {
            onSelect(value, { selected: !values.has(value) });
        }
        // Single mode should always close by select
        if (!multiple) {
            onToggleOpen(false);
        }
    };
    // ========================= Keyboard =========================
    React.useImperativeHandle(ref, () => ({
        onKeyDown: event => {
            const { which } = event;
            switch (which) {
                // >>> Arrow keys
                case KeyCode.UP:
                case KeyCode.DOWN: {
                    let offset = 0;
                    if (which === KeyCode.UP) {
                        offset = -1;
                    }
                    else if (which === KeyCode.DOWN) {
                        offset = 1;
                    }
                    if (offset !== 0) {
                        const nextActiveIndex = getEnabledActiveIndex(activeIndex + offset, offset);
                        scrollIntoView(nextActiveIndex);
                        setActive(nextActiveIndex, true);
                    }
                    break;
                }
                // >>> Select
                case KeyCode.ENTER: {
                    // value
                    const item = memoFlattenOptions[activeIndex];
                    if (item && !item.data.disabled) {
                        onSelectValue(item.data.value);
                    }
                    else {
                        onSelectValue(undefined);
                    }
                    if (open) {
                        event.preventDefault();
                    }
                    break;
                }
                // >>> Close
                case KeyCode.ESC: {
                    onToggleOpen(false);
                    if (open) {
                        event.stopPropagation();
                    }
                }
            }
        },
        onKeyUp: () => { },
        scrollTo: index => {
            scrollIntoView(index);
        }
    }));
    // ===================== Empty =====================
    let mergedNotFound;
    if (notFoundContent !== undefined) {
        mergedNotFound = notFoundContent;
    }
    else if (mode === 'combobox') {
        mergedNotFound = null;
    }
    else {
        mergedNotFound = renderEmpty('Select', searchValue ? selectLocale === null || selectLocale === void 0 ? void 0 : selectLocale.filterEmpty : undefined);
    }
    function renderItem(index) {
        const item = memoFlattenOptions[index];
        if (!item) {
            return null;
        }
        const itemData = (item.data || {});
        const { value, label, children } = itemData;
        const attrs = pickAttrs(itemData, true);
        const mergedLabel = childrenAsData ? children : label;
        return item ? (<div aria-label={typeof mergedLabel === 'string' ? mergedLabel : null} {...attrs} key={index} role="option" id={`${id}_list_${index}`} aria-selected={values.has(value)}>
                {value}
            </div>) : null;
    }
    // 是否全选状态
    let selectAllChecked = false;
    // 半选状态
    let indeterminate = false;
    // 所有enable项的值
    let enableAllValues = [];
    // 已选中的disabled项的值
    let disabledValues = [];
    // 已选中的enable项的值
    let enableValues = [];
    // 所有group key
    let groupKeys = [];
    if (multiple) {
        const enableOptions = memoFlattenOptions.filter(option => !option.data.disabled && !option.group);
        const disabledOptions = memoFlattenOptions.filter(option => option.data.disabled);
        enableAllValues = enableOptions.map(option => option.value);
        const enableOptionsValues = new Set(enableOptions.map(option => option.value));
        enableValues = [...values].filter(value => enableOptionsValues.has(value));
        const disabledOptionsValues = new Set(disabledOptions.map(option => option.value));
        disabledValues = [...values].filter(value => disabledOptionsValues.has(value));
        selectAllChecked = enableValues.length === enableOptions.length;
        indeterminate = !selectAllChecked && !!enableValues.length;
        groupKeys = memoFlattenOptions.filter(option => option.group).map(option => option.key);
    }
    let expandGroupKeys = [];
    if (defaultExpandGroupKey === true) {
        // 默认展开所有折叠项
        expandGroupKeys = groupKeys;
    }
    else if (Array.isArray(defaultExpandGroupKey)) {
        expandGroupKeys = defaultExpandGroupKey;
    }
    else if (typeof defaultExpandGroupKey === 'string' || typeof defaultExpandGroupKey === 'number') {
        expandGroupKeys = [defaultExpandGroupKey];
    }
    const [expandGroupKey, setExpandGroupKey] = React.useState(expandGroupKeys);
    const handleCheckAll = () => {
        if (searchValue === '' || searchValue === undefined) {
            triggerChange(selectAllChecked ? disabledValues : [...enableAllValues, ...disabledValues]);
        }
        else {
            let changeSelectedValues;
            if (selectAllChecked) {
                const enableAllValuesSet = new Set(enableAllValues);
                changeSelectedValues = [...values].filter(v => !enableAllValuesSet.has(v));
            }
            else {
                changeSelectedValues = Array.from(new Set([...values, ...enableAllValues]));
            }
            triggerChange(changeSelectedValues);
        }
        if (!selectAllChecked && setInnerOpen) {
            setInnerOpen(false);
        }
        cleanSearchValue && cleanSearchValue();
    };
    const getCheckStatus = (groupKey, type) => {
        const groupOptions = memoFlattenOptions.filter(option => option.groupKey === groupKey);
        const groupEnableOptions = groupOptions.filter(option => !option.data.disabled);
        if (type === 'all') {
            return groupEnableOptions.every(option => values.has(option.value));
        }
        else if (type === 'indeterminate') {
            return !groupEnableOptions.every(option => values.has(option.value))
                && groupEnableOptions.some(option => values.has(option.value));
        }
        return false;
    };
    const isKeepExpand = !multiple && keepExpand;
    const handleCheckGroup = groupKey => {
        if (!multiple) {
            return;
        }
        const groupOptions = memoFlattenOptions.filter(option => !option.disabled && option.groupKey === groupKey);
        const groupEnableOptions = groupOptions.filter(option => !option.data.disabled);
        const allValues = groupEnableOptions.map(option => option.value);
        const checkAll = getCheckStatus(groupKey, 'all');
        if (checkAll) {
            const allValuesSet = new Set(allValues);
            triggerChange([...Array.from(values).filter(value => !allValuesSet.has(value))]);
        }
        else {
            triggerChange(Array.from(new Set([...values, ...allValues])));
        }
        cleanSearchValue && cleanSearchValue();
    };
    const renderOption = (data, values, groupOption, label, itemIndex, groupKey) => {
        const _a = data, { disabled, value, title, children, style, className, 
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        maxLabelLength, extra } = _a, otherProps = __rest(_a, ["disabled", "value", "title", "children", "style", "className", "maxLabelLength", "extra"]);
        // Option
        const selected = values.has(value);
        const optionPrefixCls = `${itemPrefixCls}-option`;
        const optionClassName = classNames(itemPrefixCls, optionPrefixCls, className, {
            [`${optionPrefixCls}-grouped`]: groupOption,
            [`${optionPrefixCls}-grouped-keep-expand`]: isKeepExpand,
            [`${optionPrefixCls}-active`]: activeIndex === itemIndex && !disabled,
            [`${optionPrefixCls}-disabled`]: disabled,
            [`${optionPrefixCls}-selected`]: selected
        });
        const mergedLabel = childrenAsData ? children : label;
        // const iconVisible = !menuItemSelectedIcon || typeof menuItemSelectedIcon === 'function' || selected;
        const content = mergedLabel || value;
        let optionTitle;
        if (showTitle) {
            optionTitle = typeof content === 'string' || typeof content === 'number'
                ? content.toString()
                : undefined;
            if (title !== undefined) {
                optionTitle = title;
            }
        }
        let fContent = (typeof content === 'string' || typeof content === 'number')
            && searchValue
            ? formatContent(content, searchValue, prefixCls)
            : content;
        if (optionRender) {
            fContent = optionRender(fContent, data);
        }
        return (<div {...otherProps} aria-selected={selected} className={optionClassName} title={optionTitle} onMouseMove={() => {
                if (activeIndex === itemIndex || disabled) {
                    return;
                }
                setActive(itemIndex);
            }} onMouseLeave={() => {
                setActive(-1);
            }} onClick={() => {
                if (!disabled) {
                    onSelectValue(value);
                }
            }} style={Object.assign({ display: groupOption && !expandGroupKey.includes(groupKey) && !isKeepExpand ? 'none' : '' }, style)}>
                {multiple && (<Checkbox checked={selected} disabled={disabled}/>)}
                <div className={`${optionPrefixCls}-content`}>{fContent}</div>
                {extra}
            </div>);
    };
    const disabledVirtual = React.useMemo(() => {
        return !virtual
            || (mode === 'multiple' || mode !== 'multiple' && !keepExpand)
                && memoFlattenOptions.some(option => option.group);
    }, [virtual, mode, keepExpand, memoFlattenOptions]);
    const handleExpandChange = React.useCallback((e, key) => {
        const gIndex = expandGroupKey.indexOf(key);
        const newGroupKey = [...expandGroupKey];
        gIndex !== -1 ? newGroupKey.splice(gIndex, 1) : newGroupKey.push(key);
        setExpandGroupKey(newGroupKey);
        onExpandChange === null || onExpandChange === void 0 ? void 0 : onExpandChange(newGroupKey);
        e.stopPropagation();
    }, [expandGroupKey, onExpandChange]);
    // ========================== Render ==========================
    if (memoFlattenOptions.length === 0) {
        return (<div role="listbox" id={`${id}_list`} className={`${itemPrefixCls}-empty`} onMouseDown={onListMouseDown}>
                {mergedNotFound}
            </div>);
    }
    return (<>
            <div role="listbox" id={`${id}_list`} style={{ height: 0, width: 0, overflow: 'hidden' }}>
                {renderItem(activeIndex - 1)}
                {renderItem(activeIndex)}
                {renderItem(activeIndex + 1)}
            </div>
            {multiple && showSelectAll && (<div className={classNames(`${itemPrefixCls}-checkall`)} onClick={handleCheckAll}>
                    <Checkbox indeterminate={indeterminate} checked={selectAllChecked}/>
                    <div className={`${itemPrefixCls}-option-content`}>{selectLocale.checkAll}</div>
                </div>)}
            <List itemKey="key" ref={listRef} data={memoFlattenOptions} height={height} itemHeight={itemHeight} fullHeight={false} onMouseDown={onListMouseDown} onScroll={onScroll} virtual={!disabledVirtual} onMouseEnter={onMouseEnter}>
                {({ group, groupOption, data, groupKey }, itemIndex) => {
            const { label, key } = data;
            // Group
            if (group) {
                return (<div className={classNames(itemPrefixCls, `${itemPrefixCls}-group`, {
                        [`${itemPrefixCls}-group-keep-expand`]: isKeepExpand
                    })} onClick={() => handleCheckGroup(key)}>
                                {!isKeepExpand && (<span className={`${itemPrefixCls}-group-arrow`} onClick={e => { handleExpandChange(e, key); }}>
                                            {expandGroupKey.includes(key)
                            ? <OutlinedDown />
                            : <OutlinedRight />}
                                        </span>)}
                                {multiple && (<Checkbox className={`${itemPrefixCls}-group-checkbox`} indeterminate={getCheckStatus(key, 'indeterminate')} checked={getCheckStatus(key, 'all')}/>)}
                                {<span>{label !== undefined ? label : key}</span>}
                            </div>);
            }
            return renderOption(data, values, groupOption, label, itemIndex, groupKey);
        }}
            </List>
        </>);
};
const RefOptionList = React.forwardRef(OptionList);
RefOptionList.displayName = 'OptionList';
export default RefOptionList;
